import { useRef, useEffect, useMemo, useCallback, useState } from "react";
import { CornerDownLeft, Globe, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ChatAvatar, ChatAvatarSpacer, CHAT_AVATAR_GUTTER_CLASS } from "./ChatAvatar";
import { SidebarChatMessage, parseAttachedContextMessage } from "./SidebarChatMessage";
import { DocBreadcrumbsContext } from "./DocLink/DocBreadcrumbsContext";
import { useDocumentBreadcrumbs } from "@/domains/documents/hooks/useDocumentBreadcrumbs";
import { BuildingDataConfirmationCard } from "./BuildingDataConfirmationCard";
import type { FeedbackRating } from "./FeedbackButtons";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { ChatTextSize } from "./types";
import {
  FileTypeIcon,
  parseStatusFilename,
  parseStatusWebSearch,
  type StatusMeta,
} from "./DocLink/fileTypeIcon";
import type { IssueChipData } from "./DocLink";
import type { FileOutputChipFile } from "./FileOutputChip";
import { FileTypeIcon as DocumentFileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { UPLOADING_STATE, type PendingSend } from "@/stores/chat/ChatStore";
import type { ToolConfirmationPayload } from "@/stores/chat/repository";

/** Shown before the backend's first status event arrives — up to a few seconds
 *  while the request resolves its context. Localised: these were hardcoded
 *  English, so an Estonian user's very first pill was always in English. */
function fallbackStatusMessages(t: ReturnType<typeof useLanguage>["t"]): string[] {
  const chat = t.documents.chatSidebar;
  return [
    chat.thinking,
    chat.statusWorkingOnIt,
    chat.statusLookingIntoThat,
    chat.statusProcessing,
    chat.statusJustAMoment,
  ];
}

/** Markdown characters that would otherwise style or swallow user-supplied text. */
const MARKDOWN_SPECIALS = new Set(["\\", "`", "*", "_", "[", "]"]);

/** Escape markdown specials so a name renders as the literal string. */
function escapeMarkdown(value: string): string {
  return Array.from(value, (char) => (MARKDOWN_SPECIALS.has(char) ? `\\${char}` : char)).join("");
}

function EmptyStateSuggestions({
  questions,
  onSelect,
}: {
  questions: string[];
  onSelect?: (question: string) => void;
}) {
  const [hidden, setHidden] = useState(false);

  if (hidden || questions.length === 0) return null;

  return (
    // Indented past the greeting's mark: these continue Franklin's opening
    // line, so they start where his words do.
    <div className={cn("flex w-full flex-col items-start gap-2.5", CHAT_AVATAR_GUTTER_CLASS)}>
      {questions.map((question) => (
        <button
          key={question}
          type="button"
          onClick={() => {
            setHidden(true);
            onSelect?.(question);
          }}
          title={question}
          className={cn(
            "w-fit max-w-full whitespace-normal break-words rounded-[14px] border border-[#e2e2e4] bg-white px-3 py-2.5 text-left",
            "text-[13px] font-normal leading-[18px] text-[#262626] transition-colors hover:bg-neutral-50",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          )}
        >
          {question}
        </button>
      ))}
    </div>
  );
}

/**
 * Messages typed while Franklin was still answering. They sit under the
 * conversation wearing the user bubble's shape but muted, so they read as
 * "written, not yet asked" rather than as part of the exchange above. They are
 * rendered from the queue and never inserted into the thread, so cancelling one
 * unwinds nothing.
 */
function QueuedMessages({
  messages,
  onCancel,
  textSizeClass,
  label,
  cancelLabel,
}: {
  messages: PendingSend[];
  onCancel?: (id: string) => void;
  textSizeClass: string;
  label: string;
  cancelLabel: string;
}) {
  if (messages.length === 0) return null;

  return (
    <>
      {messages.map((message) => (
        <div
          key={message.id}
          className="flex flex-col items-end gap-1"
          data-testid="queued-message"
        >
          <div className="group/queued relative flex max-w-[85%] items-start gap-1.5">
            <div
              className={cn(
                "rounded-[10px] rounded-tr-none border border-dashed border-neutral-300",
                "bg-neutral-200/40 px-3 py-2 text-neutral-500",
                textSizeClass
              )}
            >
              <QueuedAttachments attachments={message.attachments} />
              <span className="whitespace-pre-wrap break-words">{message.text}</span>
              <span className="mt-1 block text-[10px] uppercase tracking-wide text-neutral-400">
                {label}
              </span>
            </div>
            {onCancel && (
              <button
                type="button"
                onClick={() => onCancel(message.id)}
                aria-label={cancelLabel}
                className={cn(
                  "mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded",
                  "text-neutral-400 opacity-0 transition-opacity hover:bg-neutral-200",
                  "hover:text-neutral-700 focus-visible:opacity-100 group-hover/queued:opacity-100"
                )}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

/** The files and issues a queued message will be sent with. */
function QueuedAttachments({ attachments }: { attachments: PendingSend["attachments"] }) {
  if (attachments.documents.length === 0 && attachments.issues.length === 0) return null;
  return (
    <span className="mb-1 flex flex-wrap gap-1" data-testid="queued-message-attachments">
      {attachments.documents.map((doc) => (
        <span
          key={doc.id}
          className="flex min-w-0 items-center gap-1 rounded border border-neutral-300 bg-white/60 px-1.5 py-0.5 text-xs text-neutral-600"
        >
          <DocumentFileTypeIcon
            filename={doc.filename}
            className="h-4 w-4"
            labelClassName="text-[7px]"
          />
          <span className="min-w-0 break-words">{doc.filename}</span>
          {doc.state === UPLOADING_STATE && (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
          )}
        </span>
      ))}
      {attachments.issues.map((issue) => (
        <span
          key={issue.id}
          className="rounded border border-neutral-300 bg-white/60 px-1.5 py-0.5 text-xs text-neutral-600"
        >
          #{issue.issue_number} {issue.title}
        </span>
      ))}
    </span>
  );
}

// How far off the bottom the scroll container may sit and still count as
// "the user is following along" — one line of text plus the list padding.
const NEAR_BOTTOM_PX = 64;

/**
 * Status line shown while the stream works — in the loading bubble before the
 * answer starts, and in the compact row under the draft once it has. A status
 * naming a file or a web search is prefixed with the matching icon; the
 * fallback text (only the bubble has one) never is.
 */
function StatusLine({
  statusMessage,
  statusMeta = null,
  fallback = "",
}: {
  statusMessage: string | null;
  /** Catalog key + params from the status event. Preferred over parsing the
   *  message: the prose is localised, and the regex only ever recognised two
   *  of the thirteen filename-bearing statuses. */
  statusMeta?: StatusMeta | null;
  fallback?: string;
}) {
  const display = statusMessage || fallback;
  const filename = statusMessage ? parseStatusFilename(statusMessage, statusMeta) : null;
  if (filename) {
    return (
      <>
        <FileTypeIcon filename={filename} size={14} className="flex-shrink-0" />
        {display}
      </>
    );
  }
  if (statusMessage && parseStatusWebSearch(statusMessage, statusMeta)) {
    return (
      <>
        <Globe className="flex-shrink-0 text-neutral-600" size={14} />
        {display}
      </>
    );
  }
  return <>{display}</>;
}

/**
 * Message type for the sidebar chat.
 */
export interface ChatMessage {
  id?: string | null;
  role: "user" | "assistant";
  content: string;
  sourceFilenames?: string[];
  generatedFiles?: FileOutputChipFile[];
  choices?: string[];
  choiceQuestion?: string;
  isError?: boolean;
  usedDocumentTool?: boolean;
  ehrLink?: string | null;
}

/**
 * Props for the SidebarChatMessages component.
 */
export interface SidebarChatMessagesProps {
  /** Array of chat messages to display */
  messages: ChatMessage[];
  /** Whether the chat is waiting for a response */
  isLoading: boolean;
  /** WHA-3829: building id used to resolve DocChip folder-path hover tooltips. */
  buildingId?: string;
  /** Responsive text size derived from widget width */
  textSize?: ChatTextSize;
  /** Whether sessions are being loaded */
  sessionsLoading?: boolean;
  /** EHR link URL constructed from building registry_code */
  ehrLink?: string | null;
  /** Callback when a document link is clicked in a message */
  onDocLinkClick?: (filename: string) => void;
  /** Callback when an attached-issue chip is clicked (WHA-3469) */
  onIssueClick?: (issue: IssueChipData) => void;
  /** Callback to submit feedback for a message */
  onMessageFeedback?: (messageId: string, rating: FeedbackRating) => void;
  /** Map of message IDs to current feedback rating */
  messageFeedback?: Record<string, FeedbackRating>;
  /** Message ID currently submitting feedback */
  feedbackLoadingId?: string | null;
  /** Dynamic status message from streaming (e.g. "Searching documents...") */
  statusMessage?: string | null;
  /** Catalog key + params behind `statusMessage`, used for its file icon. */
  statusMeta?: StatusMeta | null;
  /** Live-only intermediate assistant output shown while the stream is active */
  intermediateOutput?: string | null;
  /** Live-only streaming draft of the final answer (text_delta events).
   *  Replaced by the authoritative message when the stream completes. */
  streamingDraft?: string | null;
  /** Callback when a choice button is clicked */
  onChoiceSelect?: (choice: string) => void;
  /** Currently selected choice awaiting confirmation */
  pendingChoice?: string | null;
  /** Callback to confirm the pending choice */
  onChoiceConfirm?: () => void;
  /** Callback to cancel the pending choice */
  onChoiceCancel?: () => void;
  /** Callback to refresh the active chat session after an error */
  onRefreshChat?: () => void;
  /** Label for refresh chat action */
  refreshChatLabel?: string;
  /** User's first name for personalized greeting */
  firstName?: string | null;
  /** Rotates the greeting and tip pools — stable while a chat is open. */
  copyVariantSeed?: number;
  /** Messages typed mid-answer, waiting for the running turn to finish. */
  queuedMessages?: PendingSend[];
  /** Drop one queued message, by id, before it is sent. */
  onCancelQueued?: (id: string) => void;
  /** Tap-to-send example questions shown in the empty state. */
  exampleQuestions?: string[];
  /** Send one of the example questions. */
  onExampleSelect?: (question: string) => void;
  /** Callback when user submits an edited message (WHA-2637) */
  onEditLastMessage?: (editedText: string) => void;
  /** WHA-2873: Choices from AskUserQuestion tool (shown during loading) */
  askUserChoices?: string[] | null;
  /** WHA-2873: Question text from AskUserQuestion tool */
  askUserQuestion?: string | null;
  /** WHA-3481: Free-text custom answer typed into the AskUserQuestion card */
  customAnswer?: string;
  /** WHA-3481: Callback when the custom answer text changes */
  onCustomAnswerChange?: (value: string) => void;
  /** WHA-3058: the confirmation at the head of this thread's queue, and how
   *  many it is still holding. One turn raises a card per write-tool call. */
  toolConfirmation?: {
    entry: {
      questionId: string;
      payload: ToolConfirmationPayload;
    };
    total: number;
  } | null;
  /** WHA-3058: submit state for that card — it stays on screen while the POST
   *  is in flight, and after a failure so the user can try again. */
  toolConfirmationSubmission?: {
    /** The button that submit belongs to — a failed deny must not turn Approve
     *  into the retry. */
    action?: "approve" | "deny";
    state: "submitting" | "failed";
    errorKey?: "submitFailed" | "expired";
  } | null;
  /** WHA-3058: Approve the confirmation the CARD was rendered with — the id is
   *  passed back so an approval can never land on a question that replaced it
   *  at the head. Pass `{ remember: true }` (WHA-3318) to also persist
   *  auto-approve, and `overrides` (WHA-3354) for tool args edited in the card. */
  onToolConfirmationApprove?: (
    questionId: string,
    options?: { remember?: boolean; overrides?: Record<string, unknown> }
  ) => void | Promise<void>;
  /** WHA-3058: Deny that same confirmation. */
  onToolConfirmationDeny?: (questionId: string) => void | Promise<void>;
  /** WHA-3058: Acknowledge a card the backend has stopped waiting on. */
  onToolConfirmationDismiss?: (questionId: string) => void;
  /** WHA-3330: doc ids currently mid-extraction. Forwarded to each
   *  user-message SidebarChatMessage so the chip in the bubble can
   *  spin until its file finishes processing. */
  loadingDocIds?: ReadonlySet<string>;
  /** Incremented when the collapsed unread-reply launcher reopens the panel. */
  scrollToLatestReplyRequest?: number;
}

/**
 * SidebarChatMessages - Scrollable message list container for the sidebar chat.
 *
 * Features:
 * - Auto-scrolls to bottom when new messages arrive
 * - Shows welcome message when no messages
 * - Shows "Thinking..." indicator during loading
 * - Custom scrollbar styling
 *
 * Visual specs:
 * - Padding: 16px horizontal, 12px vertical
 * - Fills available space between header and input
 * - Smooth scroll behavior
 */
export function SidebarChatMessages({
  messages,
  isLoading,
  buildingId,
  textSize = "sm",
  sessionsLoading = false,
  ehrLink,
  onDocLinkClick,
  onIssueClick,
  onMessageFeedback,
  messageFeedback = {},
  feedbackLoadingId = null,
  statusMessage = null,
  statusMeta = null,
  intermediateOutput = null,
  streamingDraft = null,
  onChoiceSelect,
  pendingChoice = null,
  onChoiceConfirm,
  onChoiceCancel,
  onRefreshChat,
  refreshChatLabel,
  firstName,
  copyVariantSeed = 0,
  queuedMessages = [],
  onCancelQueued,
  exampleQuestions = [],
  onExampleSelect,
  onEditLastMessage,
  askUserChoices = null,
  askUserQuestion = null,
  customAnswer = "",
  onCustomAnswerChange,
  toolConfirmation = null,
  toolConfirmationSubmission = null,
  onToolConfirmationApprove,
  onToolConfirmationDeny,
  onToolConfirmationDismiss,
  loadingDocIds,
  scrollToLatestReplyRequest = 0,
}: SidebarChatMessagesProps) {
  const { t } = useLanguage();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevMessageCountRef = useRef(messages.length);
  const prevIsLoadingRef = useRef(isLoading);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Track which message index should animate (typewriter effect)
  // Set when isLoading transitions from true→false and a new assistant message appears
  const [animatingMessageIndex, setAnimatingMessageIndex] = useState<number | null>(null);

  // Whether a draft was on screen in the render before this one. The user
  // already watched that text stream in, so replaying it with the typewriter
  // effect would type the same answer out a second time. Written by the draft
  // effect below (not in render — a render can be thrown away), so a draft
  // discarded mid-turn by `intermediate_output`, or belonging to a thread the
  // user has since switched away from, no longer suppresses the animation.
  const hadStreamingDraftRef = useRef(false);

  // Vary the fallback deterministically so render remains pure and stable.
  const fallbackMessage = useMemo(() => {
    const options = fallbackStatusMessages(t);
    if (!isLoading) return options[0];
    return options[messages.length % options.length];
  }, [isLoading, messages.length, t]);

  // Scroll to bottom using the sentinel ref with a rAF guard for DOM readiness
  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    requestAnimationFrame(() => {
      const el = messagesEndRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior, block: "end" });
      }
    });
  }, []);

  // Scroll to bottom on initial mount when messages already exist
  // (handles collapse→expand transition where component mounts with existing messages)
  const didMountScrollRef = useRef(false);
  useEffect(() => {
    if (!didMountScrollRef.current && messages.length > 0) {
      didMountScrollRef.current = true;
      scrollToBottom("instant");
    }
  }, [messages.length, scrollToBottom]);

  useEffect(() => {
    if (scrollToLatestReplyRequest > 0) {
      scrollToBottom("instant");
    }
  }, [scrollToLatestReplyRequest, scrollToBottom]);

  // Auto-scroll to bottom only when message count changes or loading starts
  // This prevents scrolling when other state changes (like opening document preview)
  useEffect(() => {
    const messageCountChanged = messages.length !== prevMessageCountRef.current;
    const loadingStarted = isLoading && !prevIsLoadingRef.current;
    const loadingStopped = !isLoading && prevIsLoadingRef.current;

    if (messageCountChanged || loadingStarted) {
      scrollToBottom("smooth");
    }

    // Detect when loading finishes and a new assistant message appeared
    // → trigger typewriter animation on the last assistant message
    // Skip animation if message count decreased (user aborted / WHA-2637)
    if (loadingStopped && messages.length > prevMessageCountRef.current) {
      const lastMsg = messagesRef.current[messages.length - 1];
      if (lastMsg.role === "assistant" && !lastMsg.isError && !hadStreamingDraftRef.current) {
        setAnimatingMessageIndex(messages.length - 1);
      }
    }

    prevMessageCountRef.current = messages.length;
    prevIsLoadingRef.current = isLoading;
  }, [messages.length, isLoading, scrollToBottom]);

  // Whether the user is following along at the bottom. Recorded from the
  // container's own scroll events, never measured when the draft changes: by
  // the time an effect runs React has already committed the new chunk, so the
  // distance to the bottom is inflated by content the user never had a chance
  // to scroll past. One flush taller than NEAR_BOTTOM_PX (a list, a table row,
  // a code block) would then read as "scrolled away" and kill auto-follow for
  // the rest of the answer. Scrolling only changes on a real scroll — growing
  // content alone fires no event — so this flag stays true until the user
  // actually scrolls up, and flips back when they return to the bottom.
  const isAtBottomRef = useRef(true);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleScroll = () => {
      isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // Keep the view pinned to the bottom while the draft grows (draft updates
  // are already coalesced to ~80ms in the store, so this stays cheap) — but
  // only while the user is still at the bottom, so scrolling up to re-read
  // earlier messages mid-answer isn't undone every 80ms. The jump is instant
  // rather than smooth: a smooth scroll is still animating when the next flush
  // lands, and an in-flight animation would fight the following jump.
  useEffect(() => {
    hadStreamingDraftRef.current = !!streamingDraft;
    if (!streamingDraft) return;
    if (!isAtBottomRef.current) return;
    scrollToBottom("instant");
  }, [streamingDraft, scrollToBottom]);

  // Compute a conversation-wide union of all known filenames so DocChips
  // identified in earlier messages remain clickable in later messages.
  const allKnownFilenames = useMemo(() => {
    const set = new Set<string>();
    messages.forEach((m) => {
      (m.sourceFilenames ?? []).forEach((f: string) => set.add(f));
    });
    return Array.from(set);
  }, [messages]);

  // WHA-3829: filenames rendered as chips anywhere in the conversation —
  // source filenames plus user-message attachments — resolved to interactive
  // project → folder → filename breadcrumbs for the DocChip hover tooltips.
  const chipFilenames = useMemo(() => {
    const set = new Set<string>(allKnownFilenames);
    messages.forEach((m) => {
      if (m.role !== "user") return;
      const parsed = parseAttachedContextMessage(m.content);
      parsed?.attachedFilenames.forEach((f) => set.add(f));
    });
    return Array.from(set);
  }, [messages, allKnownFilenames]);
  const breadcrumbMap = useDocumentBreadcrumbs(buildingId ?? "", chipFilenames);
  const docBreadcrumbsValue = useMemo(
    () =>
      buildingId && breadcrumbMap
        ? {
            buildingId,
            getSegments: (filename: string) => breadcrumbMap.get(filename.toLowerCase()),
          }
        : null,
    [buildingId, breadcrumbMap]
  );

  const lastAssistantIndex = useMemo(
    () => messages.reduce((last, msg, i) => (msg.role === "assistant" ? i : last), -1),
    [messages]
  );

  // Index of the last user message — for edit icon (WHA-2637)
  const lastUserIndex = useMemo(
    () => messages.reduce((last, msg, i) => (msg.role === "user" ? i : last), -1),
    [messages]
  );

  // Callbacks for typewriter animation
  const handleAnimationComplete = useCallback(() => {
    setAnimatingMessageIndex(null);
  }, []);

  const handleAnimationProgress = useCallback(() => {
    scrollToBottom("smooth");
  }, [scrollToBottom]);

  // WHA-2873: Keyboard shortcuts for mid-stream AskUserQuestion choices (A/B/C/D + Enter)
  useEffect(() => {
    if (!askUserChoices?.length) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      if (e.key === "Enter" && pendingChoice) {
        e.preventDefault();
        onChoiceConfirm?.();
        return;
      }

      const index = e.key.toUpperCase().charCodeAt(0) - 65;
      if (index < 0 || index >= askUserChoices.length) return;
      e.preventDefault();
      const choice = askUserChoices[index];
      pendingChoice === choice ? onChoiceCancel?.() : onChoiceSelect?.(choice);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [askUserChoices, pendingChoice, onChoiceSelect, onChoiceCancel, onChoiceConfirm]);

  // A queued message is the user's first message on a fresh chat that is
  // still waiting to go (an upload in flight): it replaces the greeting.
  const hasMessages = messages.length > 0 || queuedMessages.length > 0;
  const textSizeClass = textSize === "lg" ? "text-[15px]" : "text-[14px]";

  // An in-progress turn can stack up to three rows — a flushed thought, the
  // draft it streamed past, and the status line under them. They are one turn,
  // so Franklin's mark is drawn once, on the topmost row that is on screen; the
  // rest hold the gutter open so every bubble keeps the same left edge.
  const showsIntermediate = Boolean(isLoading && intermediateOutput);
  const showsDraft = Boolean(isLoading && streamingDraft);

  // The empty state's greeting is one assistant message, and it is two sentences:
  // a hello, then either a plain invitation or a platform tip. Each comes from
  // its own pool, indexed by the same seed — the pools are deliberately
  // different lengths (5 and 8) so the pairings keep changing long after the
  // greetings themselves have cycled. A name is user data rendered as markdown,
  // so escape its formatting characters.
  const greetingMessage = useMemo(() => {
    const { greetings, tips } = t.documents.chatSidebar;
    const seed = Number.isFinite(copyVariantSeed) ? Math.abs(Math.trunc(copyVariantSeed)) : 0;
    const hello = firstName
      ? // A function replacement — a string one would read `$&`, `$'` and friends
        // in the name as replacement patterns (#2309).
        greetings.personalised[seed % greetings.personalised.length].replace("{firstName}", () =>
          escapeMarkdown(firstName)
        )
      : greetings.anonymous[seed % greetings.anonymous.length];
    return `${hello} ${tips[seed % tips.length]}`;
  }, [copyVariantSeed, firstName, t]);

  // WHA-3481: the AskUserQuestion card accepts either a picked option
  // (`pendingChoice`) or a typed free-text answer — Send/Cancel act on
  // whichever is set.
  const hasCustomAnswer = customAnswer.trim().length > 0;
  const hasAnswer = !!pendingChoice || hasCustomAnswer;

  // WHA-3481: auto-grow the custom-answer textarea with its content.
  const customAnswerRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const ta = customAnswerRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [customAnswer]);

  return (
    <DocBreadcrumbsContext.Provider value={docBreadcrumbsValue}>
      <div
        ref={containerRef}
        className={cn(
          "flex-1 overflow-y-auto px-4 py-3",
          "scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-transparent hover:scrollbar-thumb-neutral-400"
        )}
      >
        {sessionsLoading ? (
          // Always show centered loading spinner while sessions are loading
          <div className="flex h-full items-center justify-center text-center">
            <div className={cn("flex items-center gap-2 text-neutral-500", textSizeClass)}>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{t.documents.chatSidebar.loadingChats}</span>
            </div>
          </div>
        ) : !hasMessages ? (
          // Empty state: loading (messages loading) or welcome
          isLoading ? (
            <div className="flex h-full items-center justify-center text-center">
              <div className={cn("flex items-center gap-2 text-neutral-500", textSizeClass)}>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{t.documents.chatSidebar.loadingChats}</span>
              </div>
            </div>
          ) : (
            // Franklin's opening line, rendered through the real assistant
            // message so a fresh chat reads as a message he has already sent
            // rather than as intro text. Examples are separate bordered actions,
            // aligned to the bubble instead of sharing the message treatment.
            <div className="flex flex-col items-start gap-2.5">
              <SidebarChatMessage
                role="assistant"
                content={greetingMessage}
                textSize={textSize}
                appearance="bubble"
                compactBubble
              />
              <EmptyStateSuggestions questions={exampleQuestions} onSelect={onExampleSelect} />
            </div>
          )
        ) : (
          // Messages list
          <div className="space-y-3">
            {messages.map((message, index) => {
              const messageId = message.id ?? null;
              const isLastAssistant = index === lastAssistantIndex && !isLoading;

              return (
                <SidebarChatMessage
                  key={messageId ?? `message-${index}`}
                  messageId={messageId}
                  role={message.role}
                  content={message.content}
                  appearance={message.role === "assistant" ? "bubble" : "plain"}
                  isError={message.isError}
                  usedDocumentTool={message.usedDocumentTool}
                  ehrLink={message.ehrLink ?? ehrLink}
                  onDocLinkClick={onDocLinkClick}
                  onIssueClick={onIssueClick}
                  documentFilenames={allKnownFilenames}
                  generatedFiles={message.generatedFiles}
                  onFeedback={onMessageFeedback}
                  currentRating={messageId ? (messageFeedback[messageId] ?? null) : null}
                  isFeedbackLoading={messageId ? feedbackLoadingId === messageId : false}
                  choices={message.choices}
                  choiceQuestion={message.choiceQuestion}
                  onChoiceSelect={onChoiceSelect}
                  isLastAssistant={isLastAssistant}
                  pendingChoice={index === lastAssistantIndex ? pendingChoice : null}
                  onChoiceConfirm={onChoiceConfirm}
                  onChoiceCancel={onChoiceCancel}
                  onRefreshChat={onRefreshChat}
                  refreshChatLabel={refreshChatLabel}
                  animate={index === animatingMessageIndex}
                  onAnimationComplete={handleAnimationComplete}
                  onAnimationProgress={handleAnimationProgress}
                  textSize={textSize}
                  isLastUserMessage={
                    message.role === "user" && index === lastUserIndex && !isLoading
                  }
                  onEditMessage={
                    message.role === "user" && index === lastUserIndex && !isLoading
                      ? onEditLastMessage
                      : undefined
                  }
                  loadingDocIds={loadingDocIds}
                />
              );
            })}

            {/* Reasoning flushed mid-turn — every segment this turn has
              flushed, not just the last one. Shown alongside the draft, not
              instead of it: an `intermediate_output` flush only supersedes the
              deltas it accounts for (ChatStore's `supersedeDraft`), so with
              both present this bubble carries the flushed opening and the
              draft below carries what streamed past it. Hiding it here would
              delete the opening from the answer the user is reading. */}
            {isLoading && intermediateOutput ? (
              <SidebarChatMessage
                role="assistant"
                content={intermediateOutput}
                onDocLinkClick={onDocLinkClick}
                documentFilenames={allKnownFilenames}
                textSize={textSize}
                tone="intermediate"
                appearance="bubble"
                avatarAnimated
              />
            ) : null}

            {/* Live streaming draft — the final answer arriving token by
              token. Replaced by the authoritative message on complete, so it
              wears that message's treatment already: same bubble, same gutter,
              nothing to shift when the authoritative row takes over. */}
            {isLoading && streamingDraft ? (
              <SidebarChatMessage
                role="assistant"
                content={streamingDraft}
                onDocLinkClick={onDocLinkClick}
                documentFilenames={allKnownFilenames}
                textSize={textSize}
                appearance="bubble"
                showAvatar={!showsIntermediate}
                avatarAnimated
              />
            ) : null}

            {/* The draft replaces the loading bubble, but the turn isn't over:
              a tool call after the first tokens (a document or web search)
              still needs to say so, and its `status` events have nowhere else
              to render. Compact so it reads as a footer to the draft rather
              than a second answer. Only while there is a status to show — the
              first `text_delta` clears it, and a spinner with no label says
              nothing the draft growing above it doesn't already say. */}
            {isLoading &&
            streamingDraft &&
            statusMessage &&
            !(askUserChoices && askUserChoices.length > 0) &&
            !toolConfirmation ? (
              <div
                className={cn(
                  "flex items-center gap-1.5 text-neutral-500",
                  // The mark for this turn is already up at the draft, which
                  // may be many lines above — this row keeps its own small
                  // spinner and only borrows the gutter's width to line up.
                  CHAT_AVATAR_GUTTER_CLASS,
                  textSize === "lg" ? "text-[13px]" : "text-[12px]"
                )}
              >
                <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />
                <StatusLine statusMessage={statusMessage} statusMeta={statusMeta} />
              </div>
            ) : null}

            {/* Loading indicator — hidden when AskUserQuestion choices or a
              tool-confirmation card are shown, and while the answer is
              already streaming in as a draft. Franklin's mark animates beside
              it: the panel's one signal that he is working, with the status
              events naming what he is working on. */}
            {isLoading &&
            !showsDraft &&
            !(askUserChoices && askUserChoices.length > 0) &&
            !toolConfirmation ? (
              <div className="flex items-start gap-2">
                {showsIntermediate ? <ChatAvatarSpacer /> : <ChatAvatar animated />}

                {/* Loading bubble */}
                <div
                  className={cn(
                    "rounded-[12px] rounded-tl-[3px] bg-[#f7f7f8] px-3 py-2.5",
                    "text-[13px] text-neutral-600"
                  )}
                >
                  <span className="flex items-center gap-1.5 transition-opacity duration-200">
                    <StatusLine
                      statusMessage={statusMessage}
                      statusMeta={statusMeta}
                      fallback={fallbackMessage}
                    />
                  </span>
                </div>
              </div>
            ) : null}

            {/* WHA-2873: AskUserQuestion choice buttons (shown mid-stream).
              Indented by the avatar gutter so the question lines up with the
              assistant text rendered by SidebarChatMessage. */}
            {isLoading && askUserChoices && askUserChoices.length > 0 && (
              <div className={cn("mt-2 flex flex-col gap-1.5", CHAT_AVATAR_GUTTER_CLASS)}>
                {askUserQuestion && (
                  <p className={cn("mb-0.5 font-medium text-neutral-800", textSizeClass)}>
                    {askUserQuestion}
                  </p>
                )}
                {askUserChoices.map((choice, i) => {
                  const isSelected = pendingChoice === choice;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => (isSelected ? onChoiceCancel?.() : onChoiceSelect?.(choice))}
                      className={cn(
                        "group/choice flex w-full items-center overflow-hidden rounded-lg",
                        "border transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400",
                        isSelected
                          ? "border-primary ring-1 ring-primary/20"
                          : "border-neutral-300 hover:border-neutral-400"
                      )}
                    >
                      <span
                        className={cn(
                          "flex w-8 flex-shrink-0 items-center justify-center self-stretch",
                          "text-sm font-semibold",
                          isSelected ? "bg-primary text-white" : "bg-neutral-800 text-primary"
                        )}
                      >
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span
                        className={cn(
                          "flex-1 px-3 py-2 text-left transition-colors",
                          textSizeClass,
                          isSelected
                            ? "bg-primary/5 font-medium text-neutral-800"
                            : "bg-neutral-50 text-neutral-600 group-hover/choice:bg-neutral-100"
                        )}
                      >
                        {choice}
                      </span>
                    </button>
                  );
                })}
                {/* WHA-3481: free-text answer — an alternative to the options
                  above so the user can respond without cancelling the query.
                  Typing here clears any picked option (and vice versa). */}
                <div className="mt-1 flex items-center gap-2">
                  <span className="h-px flex-1 bg-neutral-200" />
                  <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">
                    or
                  </span>
                  <span className="h-px flex-1 bg-neutral-200" />
                </div>
                <textarea
                  ref={customAnswerRef}
                  value={customAnswer}
                  onChange={(e) => onCustomAnswerChange?.(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (hasAnswer) onChoiceConfirm?.();
                    }
                  }}
                  rows={1}
                  placeholder="Type your own answer…"
                  className={cn(
                    "max-h-[120px] min-h-[36px] w-full resize-none overflow-y-auto",
                    "rounded-lg border border-neutral-300 bg-white px-3 py-2",
                    "text-neutral-800 transition-colors placeholder:text-neutral-400",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400",
                    textSizeClass
                  )}
                />
                <div className="mt-1 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={onChoiceCancel}
                    disabled={!hasAnswer}
                    className={cn(
                      "rounded-md px-3 py-1 text-xs transition-colors",
                      hasAnswer
                        ? "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                        : "cursor-default text-neutral-300"
                    )}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onChoiceConfirm}
                    disabled={!hasAnswer}
                    className={cn(
                      "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                      "flex items-center gap-1.5",
                      hasAnswer
                        ? "bg-primary text-white hover:bg-primary/90"
                        : "cursor-default bg-neutral-200 text-neutral-400"
                    )}
                  >
                    Send
                    <CornerDownLeft className="h-3 w-3" />
                  </button>
                </div>
              </div>
            )}

            {/* WHA-3058: write-tool confirmation card. Shown while the stream
              is live — and after it ends for as long as the head card owns a
              submission of its own, because that is exactly when it must be
              readable: an expiry ends the stream a beat behind the 404, so
              gating on `isLoading` alone hid "Franklin has stopped waiting" and
              its Dismiss button, and left the card sitting in the queue until a
              later turn retired it.

              `submitting` counts as much as `failed`. A "Try again" pressed
              after the stream ended flips the state back to `submitting`, and
              dropping the card there unmounts it mid-POST — losing the local
              tool-arg overrides (the folder the user picked) and the "Always
              allow" tick, which the remounted card would then not re-send. The
              submission is head-filtered, so it can only ever be this card's. */}
            {toolConfirmation && (isLoading || toolConfirmationSubmission) ? (
              <div className={cn("mt-2", CHAT_AVATAR_GUTTER_CLASS)}>
                <BuildingDataConfirmationCard
                  // One card per question, so the head advancing REMOUNTS.
                  // Without it React keeps the instance and its state: the
                  // "Always allow" tick and the tool-arg overrides (the folder
                  // picked for card 1's file) would carry into card 2, whose
                  // sub-views seed from their payload at mount only — a write
                  // to a target the user never chose for that file.
                  key={toolConfirmation.entry.questionId}
                  payload={toolConfirmation.entry.payload}
                  count={toolConfirmation.total}
                  submission={toolConfirmationSubmission}
                  // Every answer carries the id of the question this card was
                  // rendered with, so a head that moves between render and
                  // click cannot redirect the decision onto another write.
                  onApprove={(options) =>
                    onToolConfirmationApprove?.(toolConfirmation.entry.questionId, options)
                  }
                  onDeny={() => onToolConfirmationDeny?.(toolConfirmation.entry.questionId)}
                  onDismiss={() => onToolConfirmationDismiss?.(toolConfirmation.entry.questionId)}
                  textSize={textSize}
                />
              </div>
            ) : null}

            {/* Typed while the answer above was still streaming — sent, in
              order, as soon as the turn ends. */}
            <QueuedMessages
              messages={queuedMessages}
              onCancel={onCancelQueued}
              textSizeClass={textSizeClass}
              label={t.documents.chatSidebar.queuedMessage}
              cancelLabel={t.documents.chatSidebar.cancelQueuedMessage}
            />

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
    </DocBreadcrumbsContext.Provider>
  );
}

SidebarChatMessages.displayName = "SidebarChatMessages";
